import React, { JSX } from 'react';
import { faPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

import {
  ApiBookedItem,
  ApiDirection,
  ApiFlightItem,
  ApiItemType,
  ApiSeat,
  ApiTraveler,
  ApiTravelerType
} from '@ibe/api';
import { BookedItemComponentProps } from '../../../Models/ItemRegistry';
import SummaryTabs from '../Base/SummaryTabs';
import useConfig from '../../Hooks/useConfig';
import useAppService from '../../Hooks/useAppService';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/flight-summary.en.t.json';
import Keys from '../../../_Translations/generated/flight-summary.en.t.json.keys';
import DateDisplay from '../../Components/UI/DateDisplay/DateDisplay';
import SummaryItem from '../../Components/Cart/SummaryItem/SummaryItem';
import FlightInfo from './FlightInfo';
import getGroupedTravelers from '../Base/getGroupedTravelers';
import fallbackSummary from '../../../_Translations/generated/summary.en.t.json';
import KeysSummary from '../../../_Translations/generated/summary.en.t.json.keys';

interface Props {
  children?: React.ReactNode;
}

const Summary = (props: Props & BookedItemComponentProps): JSX.Element => {
  const {
    item,
    showInfo,
    showTraveler,
    booking,
    isOpen,
    onDelete,
    withIconColumn,
    hideSinglePrices,
    customFlightDateDisplay,
    customPriceModifier,
    order,
    showNumberOfPerson,
    travelerId,
    customLabelsMapper,
    showPricePlusMinus
  } = props;
  const findFlightItem = (): ApiFlightItem | undefined => {
    const flightItems = booking.items.filter(
      i => i.itemType === ApiItemType.FLIGHT
    ) as ApiFlightItem[];
    return flightItems.find(flight => flight.id === item.idParent);
  };
  const flight = findFlightItem();
  const { t } = useTranslation('flight-summary', fallback, Keys);
  const { t: tSummary } = useTranslation('summary', fallbackSummary);

  const isRoundTrip = flight && flight.segment.length > 1;
  const firstSegment = flight?.segment[0];
  const originLeg = firstSegment?.legs[0];
  const segmentLength = flight?.segment.length || 0;
  const destinationLeg =
    flight?.segment[segmentLength - 1].legs[flight.segment[segmentLength - 1].legs.length - 1];
  const title = `${firstSegment?.origin.description} (${firstSegment?.origin.code}) - ${firstSegment?.destination.description} (${firstSegment?.destination.code})`;
  const seats = booking.bookedItems.filter(
    bookingItem => bookingItem.itemType === ApiItemType.SEAT
  );
  const config = useConfig();
  const appService = useAppService();

  const numberOfPersonLabel = showNumberOfPerson
    ? tSummary(KeysSummary.numberOfPerson, {
        count: Object.keys(item.priceByPersonId).length,
        numberOfPerson: Object.keys(item.priceByPersonId).length
      })
    : '';

  const travelerInfo = (traveler: ApiTraveler, index: number): string => {
    if (traveler.firstName && traveler.lastName) {
      return `${traveler.firstName} ${traveler.lastName}`;
    }

    switch (traveler.type) {
      case ApiTravelerType.ADULT:
        return `${t(Keys.adult)} ${index + 1}`;
      case ApiTravelerType.CHILD:
        return `${t(Keys.child)} ${index + 1}`;
      case ApiTravelerType.INFANT:
        return `${t(Keys.infant)} ${index + 1}`;
      default:
        return `${t(Keys.adult)} ${index + 1}`;
    }
  };

  const getSubtitle = (): string => {
    if (isRoundTrip) {
      return t(Keys.roundtrip);
    }

    switch (flight?.direction) {
      case ApiDirection.OUTBOUND:
        return t(Keys.outbound);
      case ApiDirection.INBOUND:
        return t(Keys.inbound);
      case ApiDirection.OUTBOUNDINBOUND:
        return t(Keys.roundtrip);
      default:
        return t(Keys.oneway);
    }
  };

  const DisplayDate = (): JSX.Element => {
    const isDisplayReturnDate = (): boolean => {
      function getToLocaleDateString(date: string): string {
        // will be cut into "2021-11-30T16:30:00.000Z" --> 2021-11-30T16:30:00
        // to avoid adding one day to the date because of time zones
        return new Date(date.substr(0, 19)).toLocaleDateString();
      }
      return (
        !!destinationLeg?.arrival &&
        !!originLeg?.departure &&
        getToLocaleDateString(destinationLeg.arrival) !== getToLocaleDateString(originLeg.departure)
      );
    };

    if (customFlightDateDisplay && flight) return customFlightDateDisplay(flight);
    if (isRoundTrip && originLeg && destinationLeg) {
      return (
        <>
          <DateDisplay date={originLeg.departure} stripTime />
          {` ${t(Keys.to)} `}
          <DateDisplay date={destinationLeg.arrival} stripTime />
        </>
      );
    }
    return (
      <>
        {originLeg && (
          <DateDisplay
            utc
            date={originLeg.departure}
            format={config.displayFormatDateTime[appService.lang.replace('-', '_')]}
          />
        )}
        {' - '}
        {destinationLeg && (
          <DateDisplay
            utc
            date={destinationLeg?.arrival}
            format={
              isDisplayReturnDate()
                ? config.displayFormatDateTime[appService.lang.replace('-', '_')]
                : 'HH:mm'
            }
          />
        )}
      </>
    );
  };

  const SsrsTabContent = (): JSX.Element => {
    return (
      <>
        {getGroupedTravelers(
          booking.travelers.filter(traveler =>
            Object.keys(item.priceByPersonId).includes(traveler.id)
          )
        )
          .map(travelersByType =>
            travelersByType.map((traveler, idx) => {
              return (
                <div key={traveler.id}>
                  {`${travelerInfo(traveler, idx)}: `}
                  {item.specialRequests
                    ? item.specialRequests
                        .filter(ssr => ssr.travelerPosition === parseInt(traveler.id, 10))
                        .map((ssr, ssrIdx) => (
                          <React.Fragment key={idx + ssrIdx + ssr.code}>
                            {ssrIdx !== 0 && <span className="ssr-comma">, </span>}
                            <span className="ssr-point">{ssr.description}</span>
                          </React.Fragment>
                        ))
                    : ''}
                </div>
              );
            })
          )
          .flat()}
      </>
    );
  };

  const SeatsTabContent = (): JSX.Element => {
    const segmentSeats: ApiBookedItem[] = seats.filter(bookedSeat => {
      const seatItem: ApiSeat = booking.items.find(
        bookingItem => bookingItem.id === bookedSeat.idParent
      ) as ApiSeat;
      return (
        seatItem.segmentId && firstSegment?.legs.map(leg => leg.id).includes(seatItem.segmentId)
      );
    });
    const infants = booking.travelers.filter(traveler => traveler.type === ApiTravelerType.INFANT);

    return (
      <>
        {getGroupedTravelers(booking.travelers)
          .map(travelersByType =>
            travelersByType.map((traveler, idx) => {
              const travelerSeats = segmentSeats.filter(
                segmentSeat => Object.keys(segmentSeat.priceByPersonId)[0] === traveler.id
              );
              return travelerSeats.map(seat => {
                const seatItem = booking.items.find(
                  bookingItem => bookingItem.id === seat?.idParent
                ) as ApiSeat;
                const leg = firstSegment?.legs.find(
                  flightLeg => flightLeg.id === seatItem.segmentId
                );
                return traveler.type === ApiTravelerType.ADULT && seat && infants[idx] ? (
                  <div key={traveler.id}>{`${travelerInfo(traveler, idx)} & ${travelerInfo(
                    infants[idx],
                    idx
                  )} (${leg?.origin.code} - ${leg?.destination.code}): ${seat.name}`}</div>
                ) : (
                  <div key={traveler.id}>{`${travelerInfo(traveler, idx)} (${leg?.origin.code} - ${
                    leg?.destination.code
                  }): ${seat.name}`}</div>
                );
              });
            })
          )
          .flat(2)}
      </>
    );
  };

  const customPrice = customPriceModifier && customPriceModifier(item);
  const customLabel = customLabelsMapper && customLabelsMapper(item);
  return item.useValuePriceDisplay ? (
    <div className="iso__summaryItem__FLIGHT__wrapper">
      <SummaryItem
        title={customLabel || item.name}
        price={
          Object.entries(item.priceByPersonId).find(([id]) => id === travelerId)?.[1] || item.price
        }
        isOpen={isOpen}
        onDelete={onDelete}
        booking={booking}
        item={item}
        order={['TITLE', 'PRICE']}
        numberOfPerson={numberOfPersonLabel}
        showPricePlusMinus={showPricePlusMinus}
      />
    </div>
  ) : (
    <SummaryItem
      title={customLabel || title}
      price={customPrice || item.price}
      subtitle={getSubtitle()}
      icon={<Icon icon={faPlane} />}
      dates={<DisplayDate />}
      isOpen={isOpen}
      onDelete={onDelete}
      booking={booking}
      item={item}
      withIconColumn={withIconColumn}
      hideSinglePrices={hideSinglePrices}
      order={order?.Flight}
      numberOfPerson={numberOfPersonLabel}
      showPricePlusMinus={showPricePlusMinus}
    >
      {flight && (showInfo || showTraveler) && (
        <SummaryTabs
          booking={booking}
          item={item}
          info={<FlightInfo flight={flight} />}
          showInfo={showInfo}
          showTraveler={showTraveler}
          showSsr
          ssrs={<SsrsTabContent />}
          showSeats={!!seats.length}
          seatTabContent={<SeatsTabContent />}
        />
      )}
    </SummaryItem>
  );
};

export default Summary;
